import { isDriveId } from "./drive";
import { sanitizeHtml } from "./sanitizeHtml";

/**
 * Files in a chat message travel as a marker inside the message HTML, so the
 * one message pipeline (1:1, group chat and channel alike — plus Mail's chat
 * dock, search and the chat-list preview) carries them without a schema
 * change:
 *
 *   <div data-fg-file="<driveItemId>" data-name="report.pdf"
 *        data-size="12345" data-type="application/pdf" data-owner="a@b.com">📎 report.pdf</div>
 *
 * DOMPurify keeps data-* attributes by default (ALLOW_DATA_ATTR), and the
 * "📎 name" text is what older clients and the chat list show. Only the id is
 * trusted, and only after it validates as a Drive id; name/size/type/owner are
 * display hints until the card resolves the real item from Drive. No URL is
 * ever read from a marker.
 *
 * Older messages (Mail's chat dock before it used markers) carry a file as a plain link instead:
 *
 *   <p>📎 <a href="{connect}/api/uploads/{objectKey}" target="_blank" rel="noreferrer noopener">name</a> (24.6 KB) — text/markdown</p>
 *   <p>📎 <a href="{drive}/api/files/{id}/download">name</a> …</p>
 *
 * `splitFileMarkers` recognises that shape too and returns it as a file with the same card; the
 * rest of the paragraph stays. Only http(s) links on the Connect or Drive API origin (or this
 * page's own origin) count; any other link stays an ordinary link.
 */
export interface FileRef {
    id: string;
    name: string;
    size: number;
    type: string;
    owner: string;
    /**
     * Set for a file that lives in Connect's own upload store (/api/uploads/...), not in Drive: the
     * absolute https URL to download it from (with the user's token). `id` is then "upload:<objectKey>".
     */
    url?: string;
}

const MARKER_ATTR = "data-fg-file";
/**
 * The spelling used before the rebrand. Messages stored then still carry it, and without this they showed as plain
 * "📎 name" text with no icon, preview or download. Read both; always write the current one.
 */
const LEGACY_MARKER_ATTR = "data-athavita-file";

function escapeAttr(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function escapeText(value: string): string {
    return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function fileMarkerHtml(ref: FileRef): string {
    return `<div ${MARKER_ATTR}="${escapeAttr(ref.id)}" data-name="${escapeAttr(ref.name)}" data-size="${Math.max(0, Math.round(ref.size))}" data-type="${escapeAttr(ref.type)}" data-owner="${escapeAttr(ref.owner)}">📎 ${escapeText(ref.name)}</div>`;
}

// ── Links written by older clients ──────────────────────────────────────────

function originOf(value: string | undefined | null): string | null {
    if (!value) return null;
    try { return new URL(value).origin; } catch { return null; }
}

/** Origins a legacy file link may point at: the Connect and Drive API hosts, and the page itself. */
export function trustedFileOrigins(): string[] {
    const out = new Set<string>();
    const env = import.meta.env as Record<string, string | undefined>;
    for (const v of [env.VITE_CONNECT_BFF_BASE_URL, env.VITE_DRIVE_API_URL ?? "https://drive.fowlguard.com"]) {
        const o = originOf(v);
        if (o) out.add(o);
    }
    if (typeof window !== "undefined" && window.location?.origin && window.location.origin !== "null") out.add(window.location.origin);
    return Array.from(out);
}

export type LegacyTarget = { kind: "drive"; id: string } | { kind: "upload"; key: string; url: string };

/** Classify a link href as a legacy Connect upload or Drive download, or null for anything else. */
export function classifyLegacyHref(href: string | null | undefined, origins: string[] = trustedFileOrigins()): LegacyTarget | null {
    if (!href) return null;
    let u: URL;
    try {
        const base = typeof window !== "undefined" && window.location?.origin !== "null" ? window.location.origin : undefined;
        u = new URL(href.trim(), base);
    } catch { return null; }
    if (u.protocol !== "https:" && u.protocol !== "http:") return null;
    if (u.username || u.password) return null;
    if (!origins.includes(u.origin)) return null;
    const drive = /^\/api\/files\/([0-9a-fA-F-]{32,36})\/download\/?$/.exec(u.pathname);
    if (drive && isDriveId(drive[1])) return { kind: "drive", id: drive[1]! };
    const upload = /^\/api\/uploads\/(.+)$/.exec(u.pathname);
    if (upload) {
        const key = upload[1]!;
        // No traversal, no empty segments, only the characters an object key has.
        if (/^[A-Za-z0-9._~%@+=,-]+(?:\/[A-Za-z0-9._~%@+=,-]+)*$/.test(key) && !key.split("/").some((s) => s === "." || s === "..")) {
            return { kind: "upload", key, url: `${u.origin}/api/uploads/${key}` };
        }
    }
    return null;
}

const UNIT: Record<string, number> = { B: 1, KB: 1024, MB: 1024 * 1024, GB: 1024 * 1024 * 1024 };
// " (24.6 KB)" then optionally " — text/markdown" (em dash, en dash or hyphen; spaces may be nbsp).
const TAIL = /^[\s\u00a0]*\(\s*([\d][\d.,]*)\s*(B|KB|MB|GB)\s*\)(?:[\s\u00a0]*[—–-][\s\u00a0]*([A-Za-z0-9][A-Za-z0-9.+_-]*\/[A-Za-z0-9](?:[A-Za-z0-9.+_-]*[A-Za-z0-9])?))?/i;
const CLIP = /\u{1F4CE}️?[\s\u00a0]*$/u;

function keyName(key: string): string {
    const last = key.split("/").pop() ?? key;
    try { return decodeURIComponent(last); } catch { return last; }
}

/**
 * Pull the "📎 <a>name</a> (size) — type" shape out of an already-sanitised document, leaving whatever else
 * the paragraph said. Several in one message are fine. Returns the files in message order.
 */
function extractLegacyFiles(doc: Document, files: FileRef[], seen: Set<string>, author: string, origins: string[]): void {
    const touched = new Set<Element>();
    for (const a of Array.from(doc.body.querySelectorAll("a[href]"))) {
        const prev = a.previousSibling;
        if (!prev || prev.nodeType !== 3 || !CLIP.test((prev as Text).data)) continue;
        const target = classifyLegacyHref(a.getAttribute("href"), origins);
        if (!target) continue;
        let size = 0;
        let type = "";
        const next = a.nextSibling;
        if (next && next.nodeType === 3) {
            const m = TAIL.exec((next as Text).data);
            if (m) {
                const n = Number.parseFloat(m[1]!.replace(/,/g, ""));
                if (Number.isFinite(n)) size = Math.round(n * (UNIT[m[2]!.toUpperCase()] ?? 1));
                type = (m[3] ?? "").toLowerCase().slice(0, 128);
                (next as Text).data = (next as Text).data.slice(m[0].length);
            }
        }
        (prev as Text).data = (prev as Text).data.replace(CLIP, "");
        const parent = a.parentElement;
        const text = (a.textContent ?? "").trim().slice(0, 512);
        a.remove();
        if (parent) touched.add(parent);
        const id = target.kind === "drive" ? target.id : `upload:${target.key}`;
        if (seen.has(id.toLowerCase())) continue;
        seen.add(id.toLowerCase());
        files.push({
            id,
            name: text || (target.kind === "upload" ? keyName(target.key) : "File"),
            size,
            type,
            owner: author,
            ...(target.kind === "upload" ? { url: target.url } : {})
        });
    }
    // A paragraph that only held the file is gone with it; a leftover "<br>" goes too.
    for (const el of touched) {
        if (!el.isConnected || el.tagName !== "P") continue;
        const rest = (el.textContent ?? "").replace(/[\s\u00a0]+/g, "");
        if (!rest && !el.querySelector("img,audio,video,[data-fg-mention]")) el.remove();
        else {
            // Drop a break left dangling at either end ("<br>text" / "text<br>").
            while (el.firstChild && (el.firstChild.nodeType === 1 && (el.firstChild as Element).tagName === "BR" || el.firstChild.nodeType === 3 && !(el.firstChild as Text).data.trim())) el.removeChild(el.firstChild);
            while (el.lastChild && (el.lastChild.nodeType === 1 && (el.lastChild as Element).tagName === "BR" || el.lastChild.nodeType === 3 && !(el.lastChild as Text).data.trim())) el.removeChild(el.lastChild);
        }
    }
}

export interface SplitOptions {
    /** Read the older "📎 link (size) — type" paragraphs as files. Off when editing, so the text keeps them. Default on. */
    legacy?: boolean;
    /** Who sent it: shown as "Shared by …" for legacy files, which carry no owner. */
    author?: string;
    /** Origins a legacy link may point at (default: the Connect and Drive hosts and this page). */
    origins?: string[];
}

/**
 * Sanitise a message body and pull its file markers out. Returns the rest of
 * the HTML (sanitised again after the markers are removed) and the validated
 * file references, in message order.
 */
export function splitFileMarkers(html: string | null | undefined, options: SplitOptions = {}): { html: string; files: FileRef[] } {
    const clean = sanitizeHtml(html);
    const hasMarker = clean.includes(MARKER_ATTR) || clean.includes(LEGACY_MARKER_ATTR);
    const hasLegacy = options.legacy !== false && clean.includes("\u{1F4CE}") && /<a\s/i.test(clean);
    if (!hasMarker && !hasLegacy) return { html: clean, files: [] };
    // DOMParser documents are inert — nothing runs and nothing loads.
    const doc = new DOMParser().parseFromString(clean, "text/html");
    const files: FileRef[] = [];
    const seen = new Set<string>();
    for (const el of Array.from(doc.body.querySelectorAll(`[${MARKER_ATTR}],[${LEGACY_MARKER_ATTR}]`))) {
        const id = el.getAttribute(MARKER_ATTR) ?? el.getAttribute(LEGACY_MARKER_ATTR) ?? "";
        el.remove();
        if (!isDriveId(id) || seen.has(id.toLowerCase())) continue;
        seen.add(id.toLowerCase());
        const size = Number.parseInt(el.getAttribute("data-size") ?? "", 10);
        files.push({
            id,
            name: (el.getAttribute("data-name") ?? "").slice(0, 512) || "File",
            size: Number.isFinite(size) && size >= 0 ? size : 0,
            type: (el.getAttribute("data-type") ?? "").slice(0, 128),
            owner: (el.getAttribute("data-owner") ?? "").slice(0, 256)
        });
    }
    if (hasLegacy) extractLegacyFiles(doc, files, seen, (options.author ?? "").slice(0, 256), options.origins ?? trustedFileOrigins());
    return { html: sanitizeHtml(doc.body.innerHTML), files };
}

/** The markers of a message, rebuilt — so an edit that rewrites the text keeps its files. */
export function fileMarkersOf(html: string | null | undefined): string {
    return splitFileMarkers(html, { legacy: false }).files.map(fileMarkerHtml).join("");
}

/** True when the visible HTML is empty (no text, no images) — a files-only message. */
export function isBlankHtml(html: string): boolean {
    if (!html) return true;
    if (/<img\b/i.test(html)) return false;
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent ?? "").trim().length === 0;
}
